import { createSelector } from 'reselect';
import { getValue } from '../constants/getFieldValues';
import {
  isPhoneValid,
  isEmailValid,
  validateDocumentIssueDate,
  validateDocumentDivisionCode,
  notEmptyField,
  validateDocumentNumber
} from './common';
import {
  getApplicant,
  isApplicantMarried,
  getApplicantLivingTogether,
  getApplicantDocumentIssuer,
  getApplicantDocumentIssueDate,
  getApplicantBirthDate,
  isApplicantDocumentIsRussianPassport,
  getApplicantDocumentNumber,
  getApplicantDocumentType,
  getApplicantDocumentDivisionCode,
  getApplicantAddress,
  getApplicantMarriageRecordHolder,
  getApplicantMarriageRecordNumber,
  getApplicantDateOfMarriage
} from '../selectors/applicant';
import {
  validateAddressAddress,
  validateAddressArea,
  validateAddressDistrict,
  validateAddressHouseAndBuilding,
  validateAddressStreet,
  validateAddressOMK,
  validateAddressFlat
} from '../selectors/address';
import { isDateInvalid } from '../helpers/dateHelpers';
import {
  validatePersonBirthPlace,
  validatePersonChangedName,
  validatePersonChangedNameDocIssuer,
  validatePersonChangedPatronimic,
  validatePersonChangedSurname,
  validatePersonChangeName
} from '../selectors/person';

import { validateDateOfMarriageWithSpousesDates } from './dates';

export const validateApplicantSex = createSelector(
  getApplicant,
  ({ sex }) => !sex.value
);

export const validateApplicantMaritalStatus = createSelector(
  getApplicant,
  ({ married: { value } }) => !value
);

export const validateApplicantPhone = createSelector(
  getApplicant,
  ({ phone }) => isPhoneValid(getValue(phone))
);

export const validateApplicantEmail = createSelector(
  getApplicant,
  ({ email }) => isEmailValid(email.value)
);

export const validateApplicantCitizenship = createSelector(
  getApplicant,
  ({ citizenship }) => !citizenship.value
);

export const validateApplicantDocumentType = createSelector(
  getApplicantDocumentType,
  type => !type
);

export const validateApplicantDocumentIssuer = createSelector(
  getApplicantDocumentIssuer,
  issuer => notEmptyField(issuer)
);

export const validateApplicantDocumentDivisionCode = createSelector(
  getApplicantDocumentDivisionCode,
  isApplicantDocumentIsRussianPassport,
  (code, isRussianPassport) =>
    validateDocumentDivisionCode(isRussianPassport, code)
);

export const validateApplicantDocumentIssuedDate = createSelector(
  getApplicantDocumentIssueDate,
  getApplicantBirthDate,
  isApplicantDocumentIsRussianPassport,
  (date, birthDate, isRussianPassport) =>
    validateDocumentIssueDate(date, birthDate, isRussianPassport)
);

export const validateApplicantDocumentNumber = createSelector(
  getApplicantDocumentNumber,
  isApplicantDocumentIsRussianPassport,
  (number, isRussianPassport) =>
    validateDocumentNumber(isRussianPassport, number)
);
export const validateApplicantBirthPlace = createSelector(
  getApplicant,
  person => validatePersonBirthPlace({ person })
);

export const validateApplicantLivingTogether = createSelector(
  isApplicantMarried,
  getApplicantLivingTogether,
  (isMarried, value) => (isMarried ? false : !value)
);

export const validateApplicantChangeName = createSelector(
  getApplicant,
  person => validatePersonChangeName({ person })
);

export const validateApplicantChangedName = createSelector(
  getApplicant,
  person => validatePersonChangedName({ person })
);

export const validateApplicantChangedSurname = createSelector(
  getApplicant,
  person => validatePersonChangedSurname({ person })
);

export const validateApplicantChangedPatronimic = createSelector(
  getApplicant,
  person => validatePersonChangedPatronimic({ person })
);

export const validateApplicantChangeNameDocIssuer = createSelector(
  getApplicant,
  person => validatePersonChangedNameDocIssuer({ person })
);

export const validateApplicantAddressOMK = createSelector(
  getApplicantAddress,
  validateAddressOMK
);

export const validateApplicantAddressArea = createSelector(
  getApplicantAddress,
  address => validateAddressArea({ address })
);

export const validateApplicantAddressFlat = createSelector(
  getApplicantAddress,
  address => validateAddressFlat({ address })
);

export const validateApplicantAddressDistrict = createSelector(
  getApplicantAddress,
  address => validateAddressDistrict({ address })
);

export const validateApplicantAddressStreet = createSelector(
  getApplicantAddress,
  address => validateAddressStreet({ address })
);

export const validateApplicantAddressHouseAndBuilding = createSelector(
  getApplicantAddress,
  address => validateAddressHouseAndBuilding({ address })
);

export const validateApplicantAddressAddress = createSelector(
  getApplicantAddress,
  address => validateAddressAddress({ address })
);

export const validateApplicantDateOfMarriage = createSelector(
  isApplicantMarried,
  getApplicantDateOfMarriage,
  validateDateOfMarriageWithSpousesDates,
  (isMarried, value, error) => {
    return isMarried && (isDateInvalid(value) || error);
  }
);

export const validateApplicantMarriageRecordHolder = createSelector(
  isApplicantMarried,
  getApplicantMarriageRecordHolder,
  (isMarried, value) => isMarried && notEmptyField(value)
);

export const validateApplicantMarriageRecordNumber = createSelector(
  isApplicantMarried,
  getApplicantMarriageRecordNumber,
  (isMarried, value) => isMarried && notEmptyField(value)
);
